import * as globals from '@wandb/weave/common/css/globals.styles';
import Color from 'color';
import type uPlot from 'uplot';
import {type Options} from 'uplot';

import {formatXAxisNonTime, formatYAxis} from '../../../util/plotHelpers/axis';
import {
  getAxisStyleForFontSize,
  type PlotFontSize,
} from '../../../util/plotHelpers/plotFontSize';
import {type Line, type Timestep} from '../../../util/plotHelpers/types';
import {getStrokeDashArrayNonSvg, getStrokeDetail} from '../lineUtils';
import {type DomainMaybe} from '../types';
import {
  createDrawIssueLinesHook,
  type IssueLinesConfig,
} from './drawIssueLines';
import {
  createDrawNonFiniteMarkersHook,
  getNonFiniteMarkers,
  type NonFiniteMarker,
  type NonFiniteMarkerState,
} from './drawNonFiniteMarkers';
import {getBands} from './getBands';
import {getDomain} from './getDomain';
import {getIncrs} from './getIncrs';
import {type LineNameMap, type SeriesEntry} from './getSeriesEntries';
import {handleInit} from './handleInit';
import {
  getuPlotLineGap,
  getuPlotTickFontSizePx,
  getuPlotTickGapPx,
  getuPlotTickSizePx,
  getuPlotXAxisTickValuesSizePx,
  getuPlotYAxisTickValuesSizePx,
} from './margins';
import {uPlotSetZoom, type Zoom} from './uPlotSetZoom';

const GRID_LINE_WIDTH_PX = 1;
export const GRID_LINE_COLOR = Color(globals.MOON_250).rgb().string();

// These match functions are for syncing the focused series across plots
// See https://github.com/leeoniya/uPlot/blob/master/demos/sync-cursor.html#L277-L286
const matchSyncKeys: uPlot.Cursor.Sync.ScaleKeyMatcher = (
  a: string | null,
  b: string | null
) => a === b;

// This makes sure that the synced series across plots is actually the same series (e.g. same line name) not just the same series index.
// This accounts for if lines are in different orders in different plots or are missing from one plot but not the other.
const matchSeriesIdxs: uPlot.Cursor.Sync.SeriesIdxMatcher = (
  sub,
  pub,
  pubSeriesIdx
) => {
  if (pubSeriesIdx == null) {
    return null;
  }

  const pubSeriesLabel = pub.series[pubSeriesIdx].label;

  return sub.series.findIndex(s => s.label === pubSeriesLabel);
};

export type NonFiniteMarkerContext = {
  markers: NonFiniteMarker[];
  stateRef: React.RefObject<NonFiniteMarkerState>;
};

export function getUPlotOptions(
  seriesEntries: (SeriesEntry | null)[],
  lineNameMap: LineNameMap,
  lines: Line[],
  nonFiniteMarkerStateRef: React.RefObject<NonFiniteMarkerState>,
  issueLinesConfig: IssueLinesConfig,
  width: number,
  height: number,
  xDomain: DomainMaybe,
  yDomain: DomainMaybe,
  xAxis: string,
  xAxisIsDatetime: boolean | undefined,
  xLogScale: boolean | undefined,
  yLogScale: boolean | undefined,
  fontSize: PlotFontSize,
  timestep: Timestep | undefined,
  syncedZooming: boolean,
  onZoom?: (zoom: Zoom) => void,
  isOverlay: boolean = false
): uPlot.Options {
  const bands = getBands(seriesEntries, lineNameMap);
  const setZoom = uPlotSetZoom(onZoom);

  // Handle infinite/NaN markers
  const nonFiniteMarkers = getNonFiniteMarkers(lines, xAxisIsDatetime ?? false);
  const drawNonFiniteMarkers = createDrawNonFiniteMarkersHook(
    nonFiniteMarkers,
    nonFiniteMarkerStateRef
  );

  // Handle issue lines
  const drawIssueLines = createDrawIssueLinesHook(issueLinesConfig);

  // First series config is for x-axis (empty object)
  // Then one config per line
  const seriesConfigs: uPlot.Series[] = [
    ...seriesEntries.map(entry => {
      if (entry == null) {
        // x-axis series
        return {};
      }
      const {type, line} = entry;
      const baseColor = line.color ?? 'red';
      const strokeDashArray = getStrokeDashArrayNonSvg(line.mark)?.map(
        num => num * window.devicePixelRatio
      );
      const {strokeWidth: lineStrokeWidth} = getStrokeDetail(
        line.color,
        isOverlay, // the axes are hidden in the overlay layer which is the highlighted layer
        line.lineWidth
      );

      if (type === 'band-min' || type === 'band-max') {
        // Band boundary series - needs visible stroke for uPlot bands to work
        return {
          show: true,
          spanGaps: true,
          stroke: Color(baseColor).rgb().string(),
          width: lineStrokeWidth,
          label: line.name,
          points: {show: false},
          sorted: 1, // Series.Sorted.Ascending - we pre-sort the data in getAlignedData, which allows some optimizations for uplot
        };
      } else {
        // Primary line series - full opacity. Dimming is handled by the useEffect in LinePlotuPlot.
        return {
          show: true,
          spanGaps: true,
          stroke: Color(baseColor).rgb().string(),
          width: lineStrokeWidth,
          label: line.name,
          sorted: 1, // Series.Sorted.Ascending - we pre-sort the data in getAlignedData, which allows some optimizations for uplot
          dash: strokeDashArray,
          points: {show: false},
        };
      }
    }),
  ];

  const cursor: Options['cursor'] = {
    drag: {
      x: true,
      y: true,
      setScale: false, // Don't auto-zoom, we handle it via onZoom callback
    },
    focus: {
      prox: 100,
    },

    sync: {
      scales: ['x', 'y'], // This makes the coords of the cursor synced to the actual x, y values, rather than the relative % width/height of the plot
      key: xAxis,
      setSeries: true,
      match: [matchSyncKeys, matchSyncKeys, matchSeriesIdxs],
      filters: {
        pub: (type: string) => {
          if (syncedZooming) {
            return true;
          }
          // don’t forward the events that start/finish a drag selection
          return type !== 'mousedown' && type !== 'mouseup';
        },
      },
    },
  };

  const tickLabelStyle = getAxisStyleForFontSize(fontSize);
  const fontString = `${getuPlotTickFontSizePx(fontSize)}px ${tickLabelStyle.fontFamily}`;

  const sharedAxisOptions = {
    gap: getuPlotTickGapPx(),
    lineGap: getuPlotLineGap(),
    ticks: {
      show: true,
      size: getuPlotTickSizePx(),
      stroke: GRID_LINE_COLOR,
      width: GRID_LINE_WIDTH_PX,
    },
    font: fontString,
    border: {show: true, stroke: GRID_LINE_COLOR, width: GRID_LINE_WIDTH_PX},
  };

  return {
    cursor,
    width: width ?? 400,
    height: height ?? 400,
    series: seriesConfigs,
    bands,
    padding: [8, 12, 0, 0], // uPlot defaults to extra padding we don't need
    axes: [
      // x-axis
      {
        stroke: tickLabelStyle.fill as string, // this is a string, not just any CSSProperties
        values: xAxisIsDatetime
          ? undefined
          : (_u: uPlot, splits: number[]) => {
              if (xDomain[0] == null || xDomain[1] == null) {
                return splits;
              }
              const formatter = formatXAxisNonTime(
                xLogScale ? 'log' : 'linear',
                xDomain[0]!,
                xDomain[1]!,
                timestep
              );
              if (formatter == null) {
                return splits;
              }
              const formattedSplits = splits.map(split =>
                split === null ? null : formatter(split)
              );
              // We only care about non-null splits appearing the same.
              const filteredFormattedSplits = formattedSplits.filter(
                split => split != null
              );
              // Just like in react-vis https://github.com/wandb/core/pull/22703
              if (
                new Set(filteredFormattedSplits).size !==
                filteredFormattedSplits.length
              ) {
                // duplicate x values, so actually just return the original splits
                return splits;
              }
              return formattedSplits;
            },
        grid: {
          show: false,
        },
        size: getuPlotXAxisTickValuesSizePx(fontSize),
        incrs: getIncrs(xAxisIsDatetime ?? false),
        ...sharedAxisOptions,
      },
      // y-axis
      {
        stroke: tickLabelStyle.fill as string, // this is a string, not just any CSSProperties
        values: (_u: uPlot, splits: number[]) => {
          return splits.map(split =>
            split == null ? null : formatYAxis(split)
          );
        },
        size: getuPlotYAxisTickValuesSizePx(fontSize),
        grid: {
          show: true,
          stroke: GRID_LINE_COLOR,
          width: GRID_LINE_WIDTH_PX,
        },
        incrs: getIncrs(false),
        ...sharedAxisOptions,
      },
    ],
    scales: {
      x: {
        time: xAxisIsDatetime ?? false,
        distr: xLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(xDomain, xLogScale ?? false),
      },
      y: {
        distr: yLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(yDomain, yLogScale ?? false),
      },
    },
    legend: {
      show: false,
    },
    hooks: {
      setSelect: [setZoom],
      init: [handleInit(onZoom)],
      draw: [drawIssueLines, drawNonFiniteMarkers],
    },
    ms: 1, // uPlot uses seconds by default, but the Line data is in milliseconds
    tzDate: (ts: number) => new Date(ts), // uPlot's ticks are off by an hour without this for some reason. TODO: file an issue.
  };
}
